import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { type MaybeRefOrGetter, toValue } from "vue";
import { request } from "@/shared/http/client";
import { noteChannelLimit } from "./channel-limit";

export interface ChannelCapabilities {
	readonly text: boolean;
	readonly media: boolean;
	readonly audio: boolean;
	readonly buttons: boolean;
	readonly quickReplies: boolean;
	readonly templates: boolean;
	readonly replyWindowHours: number | null;
	readonly maxTextLength: number;
	readonly deliveryReceipts: boolean;
	/** O que a conta publica para o PÚBLICO — vazio para um canal que só conversa. */
	readonly publishes: readonly PublishKind[];
}

export type PublishKind = "post" | "story" | "reel";

/** One box on the connection form, as the server describes it (`connect-fields.ts`). */
export interface ConnectField {
	readonly name: string;
	readonly label: string;
	readonly hint?: string;
	readonly secret?: boolean;
	readonly optional?: boolean;
	/** A instalação já sabe o valor — o formulário nem desenha a caixa. */
	readonly supplied?: boolean;
}

export interface ConnectPreset {
	readonly code: string;
	readonly label: string;
	readonly values: Record<string, string>;
	readonly hint?: string;
}

export interface ChannelSummary {
	readonly id: string;
	readonly name: string;
	readonly type: string;
	readonly provider: string;
	/**
	 * A foto da CONTA conectada — o avatar do número, da página, do perfil.
	 *
	 * O ID do arquivo, e não um endereço: quem sabe onde os bytes moram é o servidor
	 * de arquivos, e a rota autenticada que o servidor mandava antes (`/v3/files/blob/`)
	 * exige `Authorization: Bearer`, que um `<img>` nunca manda. Quem desenha resolve
	 * por `useFileSrc`.
	 *
	 * Nula até alguém escolher uma foto ou a captura acontecer — na conexão do canal,
	 * quando o provedor tem foto, e na chegada da primeira mensagem. A marca do canal
	 * cobre esse intervalo.
	 */
	readonly avatarFileId: string | null;
	/**
	 * The number, the page, the mailbox — whatever the provider calls this account.
	 *
	 * The backend has always sent it and nothing declared it, so a tenant with two
	 * WhatsApp lines had two rows called "WhatsApp" and no way to tell which was the
	 * shop's number.
	 */
	readonly accountIdentifier: string | null;
	/**
	 * `paused` is valid credentials deliberately silenced — distinct from
	 * `disconnected`, which has never been verified. Pausing stops sending and NOT
	 * receiving.
	 */
	readonly status:
		| "disconnected"
		| "pairing"
		| "connected"
		| "expired"
		| "error"
		| "paused"
		/** Desligado de propósito: fora do plano e do inbox, conversas guardadas (0296). */
		| "deactivated";
	readonly statusDetail: string | null;
	readonly canSend: boolean;
	readonly blockedReason: string | null;
	/**
	 * Whatever the provider left behind, plus the two things this screen reads: the
	 * pairing QR while `status === "pairing"`, and the assistant chosen for the
	 * channel. Both live here rather than in columns of their own — a QR is worth
	 * nothing an hour later, and the agent is a preference, not a constraint.
	 */
	readonly metadata: {
		readonly qr?: string;
		readonly agentCode?: string | null;
		/**
		 * ISO, written by the Instagram connect and by every health check that gets an
		 * answer from the provider. The row turns it into "o token vence em N dias",
		 * which is the whole difference between renewing on a Tuesday and finding out
		 * from a customer.
		 */
		readonly tokenExpiresAt?: string;
		/** O @ da conta conectada — o Instagram o escreve na conexão. */
		readonly username?: string;
		/**
		 * O NÚMERO por extenso ("+55 11 99874-5589"), escrito pela conexão Meta.
		 * `accountIdentifier` no WhatsApp oficial é o `phone_number_id` — a chave do
		 * webhook, não o telefone. Quem desenha lê `accountLabel`.
		 */
		readonly displayPhoneNumber?: string;
		readonly verifiedName?: string;
		readonly qualityRating?: string;
		/** O número que outro workspace levou: este canal parou de receber (`claimAccount`). */
		readonly releasedAccount?: string;
		/**
		 * Se a conta do provedor está PAGA, escrito pela sonda de saúde.
		 *
		 * `false` é o único erro de canal cuja resposta não está nesta tela nem no
		 * tempo: enquanto ninguém entrar no gerente da Meta, todo envio falha. A linha
		 * lhe dá uma faixa vermelha própria em vez da linha cinzenta de qualquer outro
		 * detalhe de status — que era como ele aparecia, e como ninguém o via.
		 */
		readonly billingOk?: boolean;
		/**
		 * O QUE OS ENVIOS ENSINARAM SOBRE A COBRANÇA DA META neste número.
		 *
		 * Coisa diferente de `billingOk`, que é a situação da CONTA (banida, em
		 * análise). Esta é a conta a pagar: `blocked` quer dizer que a Meta recusou
		 * um envio cobrado por não haver forma de pagamento no portfólio — o canal
		 * fica verde, o token bom, a inscrição de pé, e nada cobrado sai.
		 *
		 * Escrito pelo servidor a partir do erro 131042, porque a leitura direta do
		 * crédito é recusada pela Meta na maioria das contas. Ausente é o normal num
		 * canal novo, e não quer dizer "sem cartão".
		 */
		readonly payment?: {
			readonly status: "ok" | "blocked";
			readonly errorCode: string | null;
			readonly blockedAt: string | null;
			readonly updatedAt: string;
		};
		readonly subscribed?: boolean;
	} & Record<string, unknown>;
	/** Its public address, when published. Only a webchat ever has one. */
	readonly publicSlug: string | null;
	readonly isPublic: boolean;
	readonly capabilities: ChannelCapabilities;
}

export interface ChannelKind {
	readonly type: string;
	readonly provider: string;
	readonly available: boolean;
	/** Why it cannot be connected today, in words to put on screen. */
	readonly unavailableReason: string | null;
	/**
	 * The boxes to draw, with their labels and hints.
	 *
	 * Widened from `string[]`: the hints used to live in `ConnectGuide.vue` while the
	 * server sent bare names, so a provider that gained a field drew an unlabelled box.
	 * Both shapes are declared because the screen reads both — a deploy where the API is
	 * older than the bundle still renders a form somebody can fill in.
	 */
	readonly requires: ReadonlyArray<string | ConnectField>;
	readonly presets?: readonly ConnectPreset[];
	readonly capabilities: ChannelCapabilities;
}

export const channelKeys = {
	list: () => ["channels"] as const,
	kinds: () => ["channels", "kinds"] as const,
};

export function useChannels() {
	return useQuery({
		queryKey: channelKeys.list(),
		queryFn: async (): Promise<ChannelSummary[]> => {
			const res = await request<{ channels: ChannelSummary[] }>("/v3/channels");
			return res.channels;
		},
		// A pairing channel changes state without us asking, so poll while any is
		// mid-connection rather than leaving a stale QR on screen.
		refetchInterval: (query) =>
			(query.state.data ?? []).some((c) => c.status === "pairing") ? 3000 : false,
	});
}

/**
 * OS NÚMEROS QUE JÁ ESTÃO NA PONTE Evolution desta instalação.
 *
 * A ponte é a mesma da V2, e nela vivem os números já pareados. Sem isto,
 * conectar por QR só sabia criar: um número que já atende teria de ser pareado
 * de novo, com o telefone na mão. Lista vazia quando a instalação não tem
 * ponte própria — não há o que adotar, e a tela some sozinha.
 */
export function useEvolutionInstances(enabled: MaybeRefOrGetter<boolean> = true) {
	return useQuery({
		queryKey: evolutionInstanceKey,
		queryFn: () => request<{ instances: EvolutionInstance[] }>("/v3/channels/evolution/instances"),
		enabled: () => toValue(enabled),
		staleTime: 30_000,
	});
}

const evolutionInstanceKey = ["channels", "evolution-instances"] as const;

export interface EvolutionInstance {
	readonly name: string;
	readonly connected: boolean;
	readonly phone?: string;
	readonly profileName?: string;
	/**
	 * Este número JÁ é um canal deste workspace.
	 *
	 * É a linha que separa "adote isto" de "isto é lixo": uma instância em uso não se
	 * apaga por aqui — apaga-se removendo o canal, que é o gesto que também leva as
	 * conversas dele. As outras são o resto de tentativas de QR que ninguém leu.
	 */
	readonly inUse?: boolean;
}

/**
 * APAGAR DA PONTE UMA INSTÂNCIA QUE NUNCA VIROU CANAL.
 *
 * Pedir um QR CRIA a instância antes de alguém a ler, e desistir a meio é o desfecho
 * mais comum: o telefone não está à mão, o código vence, a pessoa vai fazer outra
 * coisa. Cada tentativa deixava um número vivo na ponte, listado para sempre em
 * "Números que já estão nesta ponte" — cinco assim no painel do dono em 10/09, sem
 * nenhuma forma de os tirar de lá sem entrar no servidor Evolution por fora.
 *
 * O servidor recusa o que não é provadamente deste workspace e o que ainda é canal
 * (`STILL_A_CHANNEL`): a ponte é partilhada, e apagar é irreversível.
 */
export function useForgetEvolutionInstance() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (name: string) =>
			request<{ removed: boolean }>(
				`/v3/channels/evolution/instances/${encodeURIComponent(name)}`,
				{ method: "DELETE" },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: evolutionInstanceKey }),
	});
}

export function useChannelKinds() {
	return useQuery({
		queryKey: channelKeys.kinds(),
		queryFn: async (): Promise<ChannelKind[]> => {
			const res = await request<{ kinds: ChannelKind[] }>("/v3/channels/kinds");
			return res.kinds;
		},
		staleTime: Number.POSITIVE_INFINITY,
	});
}

export function useConnectChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			name: string;
			type: string;
			provider: string;
			accountIdentifier?: string;
			credentials?: Record<string, string>;
		}) =>
			request<{ id: string; status: string; statusDetail?: string; qr?: string; detail?: string }>(
				"/v3/channels",
				{ method: "POST", body: input },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: channelKeys.list() }),
		/**
		 * O TETO DO PLANO é anotado onde a recusa passa, e não onde ela é desenhada.
		 *
		 * O erro continua a subir para quem chamou — o formulário do guia ainda
		 * escreve a frase dele. O que isto acrescenta é o fato de que houve uma
		 * recusa por teto, com os dois números, para a página poder oferecer o
		 * caminho de ampliar em vez de deixar a pessoa num aviso.
		 */
		onError: noteChannelLimit,
	});
}

// ---------------------------------------------------------------------------
// Health — is this channel working, and what do I press when it is not.
//
// Until now the only repair the product offered for a broken channel was DELETE,
// and that takes its conversations with it. Everything below is the alternative.
// ---------------------------------------------------------------------------

export interface ChannelProbe {
	readonly verifiedName: string | null;
	/** Meta's GREEN/YELLOW/RED, verbatim. The panel decides the colour. */
	readonly qualityRating: string | null;
	readonly tokenExpiresAt: string | null;
	readonly tokenScopes: readonly string[];
	readonly billingState: { readonly ok: boolean; readonly label: string } | null;
	/** Null means the question could not be asked — which is not the same as "no". */
	readonly webhookSubscribed: boolean | null;
	/**
	 * Uma linha por coisa que foi perguntada ao provedor, quando ele tem mais de uma
	 * a responder.
	 *
	 * É o que faz um canal de e-mail mostrar SMTP e IMAP separados: uma caixa que
	 * envia e não recebe se parecia, na tela, com uma que funciona. Opcional no fio —
	 * um canal cujo adapter ainda não sonda nada simplesmente não manda o campo.
	 */
	readonly checks?: ReadonlyArray<{
		readonly name: string;
		readonly ok: boolean;
		readonly detail: string | null;
	}>;
	readonly detail: string | null;
}

export interface ChannelHealth {
	readonly id: string;
	readonly status: ChannelSummary["status"];
	readonly statusDetail: string | null;
	readonly canSend: boolean;
	readonly blockedReason: string | null;
	readonly lastConnectedAt: string | null;
	readonly metadata: ChannelSummary["metadata"];
	readonly probe: ChannelProbe | null;
	/** What the check itself changed on the row, when it changed anything. */
	readonly reconciled: string | null;
	readonly webhook: {
		readonly url: string | null;
		readonly verifyToken: string;
		readonly signatureReady: boolean;
		/** Ver `MetaConfig.webhook.appShared`: a URL vira informação, não instrução. */
		readonly appShared?: boolean;
	};
}

/**
 * Asks the provider, on demand.
 *
 * `enabled` rather than always: the check makes up to four calls to Meta, so it runs
 * when somebody opens the panel and not on every render of the list. It is also what
 * WRITES the token expiry the row's badge reads, which is why opening the panel once
 * makes the badge appear on the list behind it.
 */
export function useChannelHealth(channelId: () => string | null) {
	return useQuery({
		queryKey: ["channels", "health", channelId] as const,
		queryFn: () => request<ChannelHealth>(`/v3/channels/${channelId()}/health`),
		enabled: () => !!channelId(),
		staleTime: 30_000,
	});
}

/**
 * A MESMA sonda, pedida por um clique — "testar conexão", do menu da linha.
 *
 * Mutação e não consulta, embora seja um `GET`: o que a pessoa pediu foi que a
 * pergunta fosse FEITA agora. `useChannelHealth` guarda a resposta por trinta
 * segundos, que é certo para um painel que abre e fecha e errado para um botão cujo
 * ponto inteiro é reperguntar. A rota escreve o que o provedor disser (é ela que move
 * a linha de volta a `connected`, ou para `error`), por isso a lista é invalidada.
 */
export function useProbeChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => request<ChannelHealth>(`/v3/channels/${id}/health`),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/**
 * Asks the provider again with the credentials already on file.
 *
 * For a WhatsApp paired by QR this is the second code the screen promised and never
 * offered — it says the code expires in about a minute, and the only way to get another
 * one was to remove the channel and connect it again, which erases every conversation
 * it ever carried.
 */
export function useReconnectChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			request<{ status: string; statusDetail?: string; qr?: string; detail?: string }>(
				`/v3/channels/${id}/reconnect`,
				// Pairing walks the provider's own handshake, which is not instant.
				{ method: "POST", timeoutMs: 60_000 },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/**
 * DESATIVAR: fora do plano e do inbox, com as conversas guardadas (0296). O meio
 * que faltava entre desconectar (ocupa a vaga) e remover (apaga as conversas).
 */
export function useDeactivateChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => request(`/v3/channels/${id}/deactivate`, { method: "POST" }),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/** Reativar pergunta ao plano antes (`CHANNEL_LIMIT`) e reconecta — na Evolution, um QR. */
export function useReactivateChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			request<{ status: string; statusDetail?: string; qr?: string; detail?: string }>(
				`/v3/channels/${id}/reactivate`,
				{ method: "POST", timeoutMs: 60_000 },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/** Off without erasing anything — never DELETE, which cascades to the conversations. */
export function useDisconnectChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => request(`/v3/channels/${id}/disconnect`, { method: "POST" }),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/** One message from this channel, to prove it end to end. It lands in the inbox. */
export function useTestMessage() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; to: string; text?: string }) =>
			request<{ status: string; externalId: string | null; detail?: string }>(
				`/v3/channels/${input.id}/test-message`,
				{ method: "POST", body: { to: input.to, ...(input.text ? { text: input.text } : {}) } },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: ["inbox"] }),
	});
}

/**
 * Subscribe the app to this channel's business account again.
 *
 * The repair for the most expensive silent failure there is: a channel with a valid
 * token whose app fell off the account connects, verifies, and never delivers one
 * inbound message.
 */
export function useResubscribeChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			request<{ subscribed: boolean | null; detail: string | null }>(
				`/v3/channels/${id}/resubscribe`,
				{ method: "POST", timeoutMs: 30_000 },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/**
 * PAGAR A META, a partir do canal — a conta que a Meta cobra por este número.
 *
 * ── POR QUE UMA CHAMADA E NÃO UM LINK FIXO NA TELA ────────────────────────
 *
 * Porque a página de formas de pagamento da Meta é endereçada pelo `business_id`
 * do portfólio DONO da conta do WhatsApp, e esse id só o servidor descobre (ele
 * tem o token e o id da conta). Uma tela que montasse a URL sozinha mandaria a
 * pessoa para o portfólio de quem calhasse estar logado no navegador — que é
 * onde ela não pode pagar nada.
 *
 * `enabled` como a sonda de saúde e pela mesma razão: são duas chamadas à Meta,
 * feitas quando alguém abre o painel e não a cada desenho da lista.
 */
export interface ChannelPayment {
	readonly wabaId: string | null;
	readonly businessId: string | null;
	readonly businessName: string | null;
	/** `unknown` é honesto e comum: a Meta recusa a leitura de crédito a provedores. */
	readonly status: "active" | "missing" | "unknown";
	readonly paymentUrl: string;
	readonly managerUrl: string | null;
	readonly errorCode: string | null;
	readonly blockedAt: string | null;
	readonly detail: string | null;
}

export function useChannelPayment(channelId: () => string | null, enabled: () => boolean) {
	return useQuery({
		queryKey: ["channels", "payment", channelId] as const,
		queryFn: () => request<ChannelPayment>(`/v3/channels/${channelId()}/payment`),
		enabled: () => !!channelId() && enabled(),
		staleTime: 60_000,
		/**
		 * Uma falha aqui não pode esvaziar o painel: o canal pode ser de um provedor
		 * que não paga a Meta, ou ter sido ligado colando um token (sem conta do
		 * WhatsApp registada). A secção simplesmente não aparece.
		 */
		retry: false,
	});
}

export function useRemoveChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => request(`/v3/channels/${id}`, { method: "DELETE" }),
		onSuccess: () => client.invalidateQueries({ queryKey: channelKeys.list() }),
	});
}

// ---------------------------------------------------------------------------
// Connecting by OAuth instead of by pasting credentials.
// ---------------------------------------------------------------------------

export interface MetaConfig {
	readonly available: boolean;
	readonly appId: string;
	readonly configId: string;
	readonly graphVersion: string;
	readonly instagramAvailable: boolean;
	readonly instagramAppId: string;
	readonly instagramRedirectUri: string;
	/**
	 * A outra porta para o mesmo lugar: o OAuth de página inteira.
	 *
	 * O Embedded Signup é melhor e nem sempre está disponível — popup bloqueado, app
	 * ainda em modo de desenvolvimento, um Business Manager cujo administrador recusa o
	 * diálogo. Sem isto, popup bloqueado era o fim da linha.
	 *
	 * `redirectUri` vem do servidor e volta para ele: a Meta compara o valor da troca do
	 * código com o do diálogo, então os dois têm de ser a MESMA string. Uma barra a mais
	 * inventada aqui derruba a troca com um erro sobre redirecionamento.
	 */
	readonly oauth?: {
		readonly available: boolean;
		readonly redirectUri: string;
		readonly scopes: readonly string[];
	};
	/**
	 * What the person has to paste into Meta's dashboard.
	 *
	 * Sent by the backend rather than built here: the API's public URL is not
	 * something the browser can know behind a proxy, and a wrong callback URL fails
	 * SILENTLY — the channel connects, the number verifies, and no message arrives.
	 */
	readonly webhook: {
		readonly url: string | null;
		readonly instagramUrl: string | null;
		readonly verifyToken: string;
		/** False means the webhook will refuse every delivery: it fails closed. */
		readonly signatureReady: boolean;
		/**
		 * O app da Meta é dividido com outro sistema.
		 *
		 * O endereço de retorno da Meta é do APLICATIVO, não do número — então colar a URL
		 * acima no painel de lá troca o callback do app inteiro e derruba a entrega de todos
		 * os canais do outro sistema, sem erro nenhum aparecer aqui. Com isto verdadeiro a
		 * tela mostra o endereço como informação e tira o botão de copiar.
		 */
		readonly appShared?: boolean;
	};
}

export interface DiscoveredWaba {
	readonly id: string;
	readonly name: string;
	readonly phones: ReadonlyArray<{
		readonly id: string;
		readonly displayPhoneNumber: string;
		readonly verifiedName: string;
		readonly qualityRating: string | null;
	}>;
}

export function useMetaConfig() {
	return useQuery({
		queryKey: ["channels", "meta-config"],
		queryFn: () => request<MetaConfig>("/v3/channels/meta/config"),
		staleTime: 300_000,
	});
}

/** O que a descoberta responde, venha o token de qual porta vier. */
export interface Discovery {
	readonly token: string;
	readonly wabas: DiscoveredWaba[];
	/** Quem autorizou, para a linha "autorizado como Fulano". Null num token de sistema. */
	readonly owner: { readonly name: string | null } | null;
	readonly expiresAt: number | null;
}

/**
 * Troca o código por um token e descobre o que ele alcança.
 *
 * `redirectUri` é OPCIONAL e a diferença importa: o Embedded Signup manda o código sem
 * redirecionamento nenhum — ele nunca existiu, o diálogo devolve o código à própria
 * página — e o OAuth de página inteira tem de mandar de volta exatamente a mesma URL
 * contra a qual o código foi emitido. Mandar uma onde não houve é o que fazia o fluxo
 * morrer na primeira chamada ao servidor.
 */
export function useMetaDiscover() {
	return useMutation({
		mutationFn: async (input: string | { code: string; redirectUri?: string }) =>
			request<Discovery>("/v3/channels/meta/discover", {
				method: "POST",
				body: typeof input === "string" ? { code: input } : input,
				// Discovery walks businesses → WABAs → numbers, one call each.
				timeoutMs: 60_000,
			}),
	});
}

export function useMetaConnect() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			name: string;
			token: string;
			wabaId: string;
			phoneNumberId: string;
			displayPhoneNumber: string;
		}) =>
			request<{ registered: boolean; subscribed: boolean; displayPhoneNumber: string }>(
				"/v3/channels/meta/connect",
				{ method: "POST", body: input, timeoutMs: 60_000 },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
		// O assistente da Meta apanha o erro e escreve-o no seu `failure`; sem isto
		// a página nunca saberia que a recusa foi o teto do plano.
		onError: noteChannelLimit,
	});
}

export function useInstagramConnect() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (code: string) =>
			request("/v3/channels/instagram/connect", {
				method: "POST",
				body: { code },
				timeoutMs: 60_000,
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
		onError: noteChannelLimit,
	});
}

// ---------------------------------------------------------------------------
// Webchat — the one channel that needs no third party, and therefore the one a
// new tenant can actually put in front of a customer today.
// ---------------------------------------------------------------------------

export interface WebchatSettings {
	readonly title: string;
	readonly greeting: string | null;
	readonly color: string;
	readonly agentCode: string | null;
	readonly askName: boolean;
	readonly askPhone: boolean;
}

export interface PublishedWebchat {
	readonly channelId: string;
	readonly slug: string;
	/**
	 * O endereço foi escolhido por alguém, e endereço escolhido não muda — o
	 * servidor recusa a troca. É o que decide se a tela ainda oferece a escolha
	 * ou apenas mostra o que já foi decidido.
	 */
	readonly slugChosen: boolean;
	readonly isPublic: boolean;
	readonly settings: WebchatSettings;
}

export function useWebchat(channelId: () => string | null) {
	return useQuery({
		queryKey: ["channels", "webchat", channelId] as const,
		queryFn: () => request<PublishedWebchat>(`/v3/channels/${channelId()}/webchat`),
		enabled: () => !!channelId(),
	});
}

export function usePublishWebchat() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (
			input: { channelId: string } & Partial<WebchatSettings> & {
					isPublic: boolean;
					/**
					 * O endereço pedido. Ausente é o que sempre foi: o servidor mantém o
					 * que o canal tem, ou monta um a partir do nome.
					 */
					slug?: string;
				},
		) => {
			const { channelId, ...body } = input;
			return request<PublishedWebchat>(`/v3/channels/${channelId}/webchat`, {
				method: "POST",
				body,
			});
		},
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["channels"] });
		},
	});
}

/**
 * O ENDEREÇO PÚBLICO de uma webchat — o único lugar do front que o monta.
 *
 * A linha do canal mostra este endereço e o painel monta os trechos de
 * incorporação em volta dele. Duas montagens divergiriam no dia em que o app
 * mudar de domínio ou a rota `/c/:slug` mudar de nome, e a que ficasse errada
 * pareceria um chat quebrado — não um link quebrado.
 */
export function publicChatUrl(slug: string): string {
	return `${window.location.origin}/c/${slug}`;
}

/**
 * The three ways to put a chat on a page, as text somebody can paste.
 *
 * Built on the front rather than returned by the API because the API does not
 * know where the app is served from — and a snippet with the wrong origin fails
 * in a way that looks like the chat is broken rather than the snippet.
 */
export function embedSnippets(slug: string, color: string) {
	const base = window.location.origin;
	const link = publicChatUrl(slug);
	return {
		link,
		iframe: `<iframe src="${link}" style="width:100%;height:600px;border:0;border-radius:12px" title="Chat"></iframe>`,
		widget: `<script src="${base}/widget.js" data-chat="${slug}" data-color="${color}"></script>`,
	};
}

/**
 * Renames a channel, replaces its credentials, pauses it, or chooses who answers.
 *
 * Until now DELETE was the only repair for an expired token — and deleting a channel
 * takes its conversations with it, `conversation` being ON DELETE CASCADE on the
 * channel. So fixing a credential threw away everyone's history.
 *
 * `agentCode: null` means NOBODY answers automatically: messages wait in the inbox
 * for the team. The old meaning — "back to whichever interaction agent is active" —
 * died with the fallback itself: an agent nobody configured used to greet real
 * customers the moment a channel connected (caso Agrobar, 12/08/2026).
 */
export function usePatchChannel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			id: string;
			name?: string;
			credentials?: Record<string, string>;
			paused?: boolean;
			agentCode?: string | null;
			/**
			 * A FOTO da conta, escolhida à mão na linha.
			 *
			 * O id de um arquivo que já subiu — nunca os bytes. `null` limpa o ponteiro e
			 * devolve o ícone da marca; o arquivo continua em Arquivos, onde a pessoa
			 * decide sobre ele com a tela que existe para isso.
			 */
			avatarFileId?: string | null;
		}) => {
			const { id, ...patch } = input;
			return request<{ channel: ChannelSummary; providerPicture?: ProviderPicture }>(
				`/v3/channels/${id}`,
				{
					method: "PATCH",
					body: patch,
					// Foto nova num WhatsApp oficial: o servidor a leva também à Meta, em
					// três chamadas, antes de responder (prazo dele: 20 s). Os 15 s de
					// sempre cortariam a resposta que diz se o WhatsApp aceitou.
					...(patch.avatarFileId ? { timeoutMs: 30_000 } : {}),
				},
			);
		},
		onSuccess: () => client.invalidateQueries({ queryKey: ["channels"] }),
	});
}

/**
 * O que o perfil do provedor disse da foto nova — só vem para o WhatsApp oficial.
 * O motivo é um código que a tela traduz; `detail` é a frase crua da Meta.
 */
export type ProviderPicture =
	| { readonly status: "updated" }
	| {
			readonly status: "failed";
			readonly reason:
				| "no_credentials"
				| "image_unreadable"
				| "expired"
				| "permission"
				| "refused"
				| "unreachable";
			readonly detail: string;
	  };
